<template>
  <v-dialog :retain-focus="false" :value="sectionEditor.show" :width="1000" @input="handleClosePopup">
    <div style="background-color: #fff">
      <div class="header-modal">
        <v-card-title class="d-flex justify-space-between align-center" style="padding: 10px 0">
          <span style="color: #0081c7">{{ fields.title }}</span>
          <v-btn icon @click="handleClosePopup">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
      </div>

      <v-card-text style="max-height: 70vh; overflow-y: auto">
        <div class="mb-4">
          <label class="label-input">Section</label>
          <v-row>
            <v-col cols="12" md="8">
              <v-text-field v-model="fields.title" label="Name" hide-details />
            </v-col>
            <v-col cols="12" md="4">
              <v-checkbox v-model="fields.enableAnchor" label="Enable Anchor" hide-details />
            </v-col>
          </v-row>
        </div>

        <div class="mb-4">
          <SectionSettingsContainerFuild :settings="fields.settings" :container-page="fields.containerPage" />
        </div>

        <div class="mb-4">
          <label class="label-input">Layout</label>
          <div class="group-item">
            <v-checkbox v-model="fields.containerFullWidth" label="Container Full Width" hide-details dense />
          </div>
        </div>

        <div class="mb-4">
          <label class="label-input">Top Row Images</label>
          <v-row>
            <v-col cols="12" md="4">
              <div class="group-item">
                <div class="font-weight-bold mb-2">Image 1</div>
                <UploadImage
                  @callback="obj => handleUploadImage('image1', obj)"
                  :image="{ imageSrc: fields.images.image1 }"
                  :height="`180`"
                  @callbackDelete="handleRemoveImage('image1')"
                />
              </div>
            </v-col>
            <v-col cols="12" md="4">
              <div class="group-item">
                <div class="font-weight-bold mb-2">Image 2</div>
                <UploadImage
                  @callback="obj => handleUploadImage('image2', obj)"
                  :image="{ imageSrc: fields.images.image2 }"
                  :height="`180`"
                  @callbackDelete="handleRemoveImage('image2')"
                />
              </div>
            </v-col>
            <v-col cols="12" md="4">
              <div class="group-item">
                <div class="font-weight-bold mb-2">Image 3</div>
                <UploadImage
                  @callback="obj => handleUploadImage('image3', obj)"
                  :image="{ imageSrc: fields.images.image3 }"
                  :height="`180`"
                  @callbackDelete="handleRemoveImage('image3')"
                />
              </div>
            </v-col>
          </v-row>
        </div>

        <div class="mb-4">
          <label class="label-input">Text Box</label>
          <div class="group-item">
            <div class="font-weight-bold mb-2">Icon</div>
            <UploadImage
              @callback="handleUploadBoxIcon"
              :image="{ imageSrc: fields.box.icon }"
              :height="`80`"
              @callbackDelete="handleRemoveBoxIcon"
            />
            <v-row class="mt-4">
              <v-col cols="12" md="4">
                <v-select
                  v-model="fields.box.iconPosition"
                  :items="iconPositionOptions"
                  label="Icon position"
                  hide-details
                />
              </v-col>
              <v-col cols="12" md="4">
                <v-text-field
                  type="number"
                  v-model.number="fields.box.iconSizeDesktop"
                  label="Icon size (Desktop)"
                  min="1"
                  hide-details
                  suffix="px"
                />
              </v-col>
              <v-col cols="12" md="4">
                <v-text-field
                  type="number"
                  v-model.number="fields.box.iconSizeMobile"
                  label="Icon size (Mobile)"
                  min="1"
                  hide-details
                  suffix="px"
                />
              </v-col>
            </v-row>
            <v-row class="mt-4">
              <v-col cols="12" md="6">
                <ColorPicker
                  v-model="fields.box.backgroundColor"
                  name="boxBackgroundColor"
                  label="Background color"
                  default-color="#ffffff"
                />
              </v-col>
              <v-col cols="12" md="6">
                <ColorPicker
                  v-model="fields.box.textColor"
                  name="boxTextColor"
                  label="Text color"
                  default-color="#000000"
                />
              </v-col>
            </v-row>
            <div class="mt-4">
              <div class="font-weight-bold mb-2">Text</div>
              <ckeditor :editor="editor" v-model="fields.box.text" :config="editorConfig" />
            </div>
          </div>
        </div>

        <div class="mb-4">
          <label class="label-input">Bottom Row Images</label>
          <v-row>
            <v-col cols="12" md="6">
              <div class="group-item">
                <div class="font-weight-bold mb-2">Image 4</div>
                <UploadImage
                  @callback="obj => handleUploadImage('image4', obj)"
                  :image="{ imageSrc: fields.images.image4 }"
                  :height="`180`"
                  @callbackDelete="handleRemoveImage('image4')"
                />
              </div>
            </v-col>
            <v-col cols="12" md="6">
              <div class="group-item">
                <div class="font-weight-bold mb-2">Image 5</div>
                <UploadImage
                  @callback="obj => handleUploadImage('image5', obj)"
                  :image="{ imageSrc: fields.images.image5 }"
                  :height="`180`"
                  @callbackDelete="handleRemoveImage('image5')"
                />
              </div>
            </v-col>
          </v-row>
        </div>
      </v-card-text>

      <div class="footer-modal">
        <v-card-actions style="padding: 10px 0">
          <v-spacer></v-spacer>
          <v-btn min-width="100" depressed @click="handleClosePopup">Cancel</v-btn>
          <v-btn min-width="100" color="primary" @click="handleSave">Save</v-btn>
        </v-card-actions>
      </div>
    </div>
  </v-dialog>
</template>

<script>
import Editor from 'ckeditor5-custom-build/build/ckeditor';
import { ckeditorConfigFullToolbar } from '@/helpers/editor';
import { updateHtmlFromData } from '../common/html/update.js';
import UploadImage from '@/components/form/uploadFile/UpdateImage.vue';
import ColorPicker from './common/color-picker.vue';
import SectionSettingsContainerFuild from './common/section-settings-container-fuild.vue';

export default {
  name: 'losaprily-images-grid-section-editor',
  components: {
    SectionSettingsContainerFuild,
    UploadImage,
    ColorPicker,
  },
  props: {
    sectionEditor: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      editor: Editor,
      editorConfig: ckeditorConfigFullToolbar,
      iconPositionOptions: ['left', 'center', 'right'],
      fields: {
        title: 'Losaprily images grid',
        enableAnchor: false,
        settings: {
          paddingTop: true,
          paddingBottom: true,
          backgroundColor: '#dbf1ff',
          textColor: '#000000',
          overlayColor: '#000000',
          overlayOpacity: 0,
          showOverlay: false,
          backgroundImage: '',
        },
        containerPage: {
          enableStyle: false,
          background: '#FFFFFF',
        },
        images: {
          image1: '',
          image2: '',
          image3: '',
          image4: '',
          image5: '',
        },
        box: {
          icon: '',
          text: '',
          backgroundColor: '#ffffff',
          textColor: '#0066b3',
          iconSizeDesktop: 48,
          iconSizeMobile: 30,
          iconPosition: 'center',
        },
        containerFullWidth: false,
      },
    };
  },
  mounted() {
    this.applyExtract(this.sectionEditor.sectionExtract);
  },
  watch: {
    sectionEditor: {
      handler(newVal) {
        if (newVal && newVal.sectionExtract) {
          this.applyExtract(newVal.sectionExtract);
        }
      },
      deep: true,
    },
  },
  methods: {
    applyExtract(extract) {
      if (!extract) return;
      this.fields = {
        ...this.fields,
        ...extract,
        settings: { ...this.fields.settings, ...(extract.settings || {}) },
        containerPage: { ...this.fields.containerPage, ...(extract.containerPage || {}) },
        images: { ...this.fields.images, ...(extract.images || {}) },
        box: { ...this.fields.box, ...(extract.box || {}) },
      };
    },
    handleUploadImage(key, obj) {
      if (!obj || !obj.imageSrc) return;
      this.fields.images[key] = obj.imageSrc;
    },
    handleRemoveImage(key) {
      this.fields.images[key] = '';
    },
    handleUploadBoxIcon(obj) {
      if (!obj || !obj.imageSrc) return;
      this.fields.box.icon = obj.imageSrc;
    },
    handleRemoveBoxIcon() {
      this.fields.box.icon = '';
    },
    handleClosePopup() {
      this.$emit('close');
    },
    handleSave() {
      const updateHtml = updateHtmlFromData.sectionLosaprilyImagesGrid(this.sectionEditor.section.html, this.fields);
      this.$emit('update:section', {
        ...this.sectionEditor.section,
        title: this.fields.title,
        html: updateHtml,
      });
      this.$emit('close');
    },
  },
};
</script>

<style scoped lang="scss">
.group-item {
  padding: 15px 10px;
  border-radius: 10px;
  border: 1px solid #e0e0e0;
}
.label-input {
  font-size: 18px !important;
  font-weight: 500;
  color: #000;
  margin-bottom: 10px;
  display: block;
}
.header-modal {
  position: sticky;
  top: 0;
  left: 0;
  z-index: 999;
  background-color: #fff;
  padding: 0 16px;
  box-shadow: 0px 0px 12px #0000000f;
}
.footer-modal {
  position: sticky;
  padding: 0 16px;
  bottom: 0;
  left: 0;
  z-index: 999;
  background-color: #fff;
  box-shadow: 0px 0px 12px #0000000f;
}
</style>
